import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Badge,
  alpha,
  useTheme,
  Collapse,
} from '@mui/material'
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  LocalShipping as ShipmentIcon,
  Business as AccountsIcon,
  Map as MapIcon,
  Schedule as CronIcon,
  Gavel as DisputeIcon,
  AccountBalance as FinanceIcon,
  MoneyOff as PenaltiesIcon,
  CardMembership as SubscriptionIcon,
  Settings as SettingsIcon,
  VerifiedUser as KycIcon,
  Notifications as NotificationsIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  ChevronLeft as ChevronLeftIcon,
  ExpandLess,
  ExpandMore,
  LocalShipping as FreighterIcon,
  Store as ShipperIcon,
  Assignment as AssignmentIcon,
  Badge as BadgeIcon,
  DirectionsCar as VehicleIcon,
  Stars as StarsIcon,
  HealthAndSafety as HealthIcon,
  OpenInNew as OpenInNewIcon,
  Tab as TabIcon,
  Launch as LaunchIcon,
  Sms as SmsIcon,
  Stairs as LadderIcon,
} from '@mui/icons-material'
import { useAuthStore } from '../store/authStore'
import { useEnumStore } from '../store/enumStore'
import { useThemeMode } from '../theme/ThemeContext'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'

const drawerWidth = 260
const collapsedWidth = 72

interface LayoutProps {
  children: React.ReactNode
}

interface NavItem {
  text: string
  icon: React.ReactNode
  path?: string
  badge?: number
  children?: { text: string; path: string; icon?: React.ReactNode }[]
}

export default function Layout({ children }: LayoutProps) {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const { mode, toggleTheme } = useThemeMode()
  const { user, logout } = useAuthStore()
  const { clearEnums } = useEnumStore()
  const getLocalizedLabel = useLocalizedLabel()
  const { t } = useTranslation()

  const [mobileOpen, setMobileOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null)
  const [detachAnchor, setDetachAnchor] = useState<null | HTMLElement>(null)

  /** Absolute URL of the page the operator is currently looking at,
   *  including any query string. Used by the detach controls so the new
   *  tab / window opens on the same view. */
  const currentPageUrl = () =>
    `${window.location.origin}${location.pathname}${location.search}${location.hash}`

  const openInNewTab = () => {
    window.open(currentPageUrl(), '_blank', 'noopener,noreferrer')
    setDetachAnchor(null)
  }

  const openInNewWindow = () => {
    const features = [
      'noopener',
      'noreferrer',
      'width=1400',
      'height=900',
      `left=${Math.max(0, Math.round((window.screen.availWidth - 1400) / 2))}`,
      `top=${Math.max(0, Math.round((window.screen.availHeight - 900) / 2))}`,
      'menubar=no',
      'toolbar=no',
      'location=no',
      'status=no',
      'resizable=yes',
      'scrollbars=yes',
    ].join(',')
    window.open(currentPageUrl(), '_blank', features)
    setDetachAnchor(null)
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleCollapseToggle = () => {
    setCollapsed(!collapsed)
    setOpenSubMenu(null)
  }

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    handleProfileMenuClose()
    clearEnums()
    logout()
    navigate('/login')
  }

  const handleSubMenuToggle = (text: string) => {
    if (collapsed) {
      setCollapsed(false)
    }
    setOpenSubMenu(openSubMenu === text ? null : text)
  }

  const navItems: NavItem[] = [
    { text: t('nav.dashboard'), icon: <DashboardIcon />, path: '/' },
    {
      text: t('nav.shipments'),
      icon: <ShipmentIcon />,
      children: [
        { text: t('nav.statistics'), path: '/shipments', icon: <ShipmentIcon /> },
        { text: t('nav.shipmentRequests'), path: '/shipment-requests', icon: <ShipmentIcon /> },
        { text: t('nav.draftRequests'), path: '/draft-requests', icon: <ShipmentIcon /> },
        { text: t('nav.jobs'), path: '/jobs', icon: <ShipmentIcon /> },
        { text: t('nav.jobOffers'), path: '/job-offers', icon: <AssignmentIcon /> },
        { text: t('nav.smsOptOuts'), path: '/receiver-sms-opt-outs', icon: <SmsIcon /> },
        { text: t('nav.cargoReturns'), path: '/cargo-returns', icon: <AssignmentIcon /> },
        { text: t('nav.mitigationLadder'), path: '/mitigation', icon: <LadderIcon /> },
      ],
    },
    {
      text: t('nav.accounts'),
      icon: <AccountsIcon />,
      children: [
        { text: t('nav.allAccounts'), path: '/accounts', icon: <AccountsIcon /> },
        { text: t('nav.shippers'), path: '/accounts?type=SHIPPER', icon: <ShipperIcon /> },
        { text: t('nav.freighters'), path: '/accounts?type=FREIGHTER', icon: <FreighterIcon /> },
        { text: t('nav.drivers'), path: '/drivers', icon: <BadgeIcon /> },
        { text: t('nav.vehicles'), path: '/vehicles', icon: <VehicleIcon /> },
      ],
    },
    { text: t('nav.liveTracking'), icon: <MapIcon />, path: '/maps' },
    { text: t('nav.verifications'), icon: <KycIcon />, path: '/pending-verifications' },
    { text: t('nav.disputes'), icon: <DisputeIcon />, path: '/disputes' },
    { text: t('nav.scores'), icon: <StarsIcon />, path: '/scores' },
    { text: t('nav.finance'), icon: <FinanceIcon />, path: '/finance' },
    { text: t('nav.penalties'), icon: <PenaltiesIcon />, path: '/penalties' },
    {
      text: t('nav.subscriptions'),
      icon: <SubscriptionIcon />,
      children: [
        { text: t('nav.activeSubscriptions'), path: '/subscriptions' },
        { text: t('nav.plans'), path: '/subscription-plans' },
        { text: t('nav.entitlements'), path: '/entitlements' },
        { text: t('nav.campaigns'), path: '/campaigns' },
        { text: t('nav.billingControl'), path: '/billing-settings' },
      ],
    },
    { text: t('nav.notifications'), icon: <NotificationsIcon />, path: '/notifications' },
    { text: t('nav.cronJobs'), icon: <CronIcon />, path: '/cron-jobs' },
    { text: t('nav.systemHealth'), icon: <HealthIcon />, path: '/system-health' },
    { text: t('nav.systemConfiguration'), icon: <SettingsIcon />, path: '/config' },
  ]

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path.split('?')[0])
  }

  const actualDrawerWidth = collapsed ? collapsedWidth : drawerWidth

  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.mode === 'dark' ? '#0f172a' : '#ffffff',
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          minHeight: 64,
        }}
      >
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ShipmentIcon sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                LogarGo
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                {t('app.adminConsole')}
              </Typography>
            </Box>
          </Box>
        )}
        <IconButton onClick={handleCollapseToggle} size="small">
          <ChevronLeftIcon
            sx={{
              transform: collapsed ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.3s',
            }}
          />
        </IconButton>
      </Box>

      <Divider />

      {/* Navigation */}
      <List sx={{ flex: 1, px: 1, py: 2, overflowY: 'auto' }}>
        {navItems.map((item) => {
          const hasChildren = item.children && item.children.length > 0
          const isOpen = openSubMenu === item.text
          const active = item.path ? isActive(item.path) : item.children?.some((c) => isActive(c.path))

          return (
            <Box key={item.text}>
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <Tooltip title={collapsed ? item.text : ''} placement="right" arrow>
                  <ListItemButton
                    onClick={() => {
                      if (hasChildren) {
                        handleSubMenuToggle(item.text)
                      } else if (item.path) {
                        navigate(item.path)
                        setMobileOpen(false)
                      }
                    }}
                    sx={{
                      minHeight: 44,
                      borderRadius: 2,
                      px: collapsed ? 2 : 2,
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                      color: active ? theme.palette.primary.main : theme.palette.text.primary,
                      '&:hover': {
                        bgcolor: active
                          ? alpha(theme.palette.primary.main, 0.15)
                          : alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: collapsed ? 0 : 40,
                        color: active ? theme.palette.primary.main : theme.palette.text.secondary,
                      }}
                    >
                      {item.badge ? (
                        <Badge badgeContent={item.badge} color="error" max={99}>
                          {item.icon}
                        </Badge>
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>
                    {!collapsed && (
                      <>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: '0.875rem',
                            fontWeight: active ? 600 : 500,
                          }}
                        />
                        {hasChildren && (isOpen ? <ExpandLess /> : <ExpandMore />)}
                      </>
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>

              {/* Sub Menu */}
              {hasChildren && !collapsed && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 2 }}>
                    {item.children?.map((child) => (
                      <ListItemButton
                        key={child.path}
                        onClick={() => {
                          navigate(child.path)
                          setMobileOpen(false)
                        }}
                        sx={{
                          minHeight: 38,
                          borderRadius: 2,
                          pl: 3,
                          mb: 0.25,
                          bgcolor: isActive(child.path) ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                          },
                        }}
                      >
                        {child.icon && (
                          <ListItemIcon sx={{ minWidth: 32, color: theme.palette.text.secondary }}>
                            {child.icon}
                          </ListItemIcon>
                        )}
                        <ListItemText
                          primary={child.text}
                          primaryTypographyProps={{
                            fontSize: '0.8125rem',
                            fontWeight: isActive(child.path) ? 600 : 400,
                            color: isActive(child.path) ? theme.palette.primary.main : theme.palette.text.secondary,
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}
            </Box>
          )
        })}
      </List>

      {/* User Section */}
      {!collapsed && (
        <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: theme.palette.primary.main,
                fontSize: '0.875rem',
              }}
            >
              {user?.name?.charAt(0).toUpperCase() || 'A'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {user?.name || 'Admin User'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {user?.roles?.[0] ? getLocalizedLabel('userRole', user.roles[0]) : t('topbar.administrator')}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', flexGrow: 1, width: '100%', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${actualDrawerWidth}px)` },
          ml: { sm: `${actualDrawerWidth}px` },
          bgcolor: 'background.paper',
          borderBottom: `1px solid ${theme.palette.divider}`,
          transition: 'width 0.3s, margin-left 0.3s',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' }, color: 'text.primary' }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={t('topbar.detach')}>
              <IconButton
                onClick={(e) => setDetachAnchor(e.currentTarget)}
                sx={{ color: 'text.secondary' }}
                aria-haspopup="menu"
                aria-expanded={Boolean(detachAnchor)}
              >
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={mode === 'light' ? t('topbar.switchToDark') : t('topbar.switchToLight')}>
              <IconButton onClick={toggleTheme} sx={{ color: 'text.secondary' }}>
                {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
              </IconButton>
            </Tooltip>

            <Tooltip title={t('topbar.notifications')}>
              <IconButton sx={{ color: 'text.secondary' }}>
                <Badge badgeContent={3} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <IconButton onClick={handleProfileMenuOpen} sx={{ ml: 1 }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: theme.palette.primary.main,
                  fontSize: '0.875rem',
                }}
              >
                {user?.name?.charAt(0).toUpperCase() || 'A'}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Detach Menu */}
      <Menu
        anchorEl={detachAnchor}
        open={Boolean(detachAnchor)}
        onClose={() => setDetachAnchor(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: { mt: 1, minWidth: 220, borderRadius: 2 },
        }}
      >
        <MenuItem onClick={openInNewTab}>
          <ListItemIcon>
            <TabIcon fontSize="small" />
          </ListItemIcon>
          {t('topbar.openNewTab')}
        </MenuItem>
        <MenuItem onClick={openInNewWindow}>
          <ListItemIcon>
            <LaunchIcon fontSize="small" />
          </ListItemIcon>
          {t('topbar.openNewWindow')}
        </MenuItem>
      </Menu>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {user?.name || 'Admin User'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email || user?.phone}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            handleProfileMenuClose()
            navigate('/settings')
          }}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          {t('topbar.profileSettings')}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          {t('topbar.logout')}
        </MenuItem>
      </Menu>

      {/* Sidebar - Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Sidebar - Desktop */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            width: actualDrawerWidth,
            boxSizing: 'border-box',
            transition: 'width 0.3s',
            overflowX: 'hidden',
          },
        }}
        open
      >
        {drawer}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', sm: `calc(100% - ${actualDrawerWidth}px)` },
          ml: { xs: 0, sm: `${actualDrawerWidth}px` },
          minHeight: '100vh',
          transition: 'width 0.3s ease, margin-left 0.3s ease',
        }}
      >
        <Toolbar />
        <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {children}
        </Box>
      </Box>
    </Box>
  )
}
